import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';

// Un filtre = une étiquette qu'on lit (« Catégorie : RX, Inter ») et qu'on
// retire d'un clic. Vide, elle dit ce qu'on peut filtrer.
export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterChipProps {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}

export function FilterChip({ label, options, selected, onChange }: FilterChipProps) {
  const { t } = useTranslation();
  const active = selected.length > 0;
  const summary = options
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label)
    .join(', ');

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  return (
    <div
      className={cn(
        'inline-flex h-9 items-center rounded-md border text-sm transition-colors',
        active ? 'border-white/25 text-foreground' : 'border-dashed border-white/[0.16] text-muted-foreground',
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex h-full items-center gap-1.5 pl-3 pr-2 hover:text-foreground">
            <span className={cn(active && 'text-muted-foreground')}>{label}</span>
            {active && <span className="max-w-[220px] truncate">{summary}</span>}
            {!active && <ChevronDown size={14} />}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 p-1">
          <div role="group" aria-label={label} className="max-h-72 overflow-y-auto">
            {options.map((o) => {
              const id = `filter-${label}-${o.value}`;
              return (
                <label
                  key={o.value}
                  htmlFor={id}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-white/[0.05]"
                >
                  <Checkbox id={id} checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.count !== undefined && <span className="text-xs text-muted-foreground tabular-nums">{o.count}</span>}
                </label>
              );
            })}
          </div>
          {active && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full rounded-md px-2 py-2 text-left text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            >
              {t('dataTable.clearFilter')}
            </button>
          )}
        </PopoverContent>
      </Popover>
      {active && (
        <button
          type="button"
          onClick={() => onChange([])}
          aria-label={t('dataTable.removeFilter', { label })}
          className="flex h-full items-center pr-2 pl-1 text-muted-foreground hover:text-foreground"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
